import type { ListTaxonomyNodesResponse } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { TAXONOMY_CHILDREN_QUERY_KEY } from './useGetTaxonomyChildren';

export const useDeleteTaxonomyNode = ({
  taxonomyId,
  onSuccess,
  onError,
}: {
  taxonomyId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending, error } = useMutation({
    mutationFn: (nodeId: string) => api.taxonomy.deleteTaxonomyNode(tenantId, taxonomyId, nodeId),
    onMutate: async (nodeId: string) => {
      const previousData = queryClient.getQueriesData({ queryKey: [TAXONOMY_CHILDREN_QUERY_KEY] });

      queryClient.setQueriesData<InfiniteData<AxiosResponse<ListTaxonomyNodesResponse>>>(
        { queryKey: [TAXONOMY_CHILDREN_QUERY_KEY] },
        old => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: {
                ...page.data,
                nodes: page.data.nodes.filter(node => node.id !== nodeId),
                pageData: {
                  ...page.data.pageData,
                  total: Math.max(0, page.data.pageData.total - 1),
                },
              },
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (error: AxiosError<{ message?: string }>, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      onError?.(error.response?.data?.message ?? 'Failed to delete category');
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [TAXONOMY_CHILDREN_QUERY_KEY],
        exact: false,
      });
    },
  });

  return {
    mutate,
    isPending,
    error,
  };
};
